import { Alert, Button, Card, Flex, Form, FormInstance, Input, Modal, notification, Select, Skeleton, Space, Spin, Tooltip } from "antd";
import React from "react";
import ListApi from "../../../api-services/list-api.service";
import Icon from "@mdi/react";
import { mdiEmailOutline, mdiMinus, mdiPhoneInTalkOutline, mdiPlus, mdiWhatsapp } from "@mdi/js";
import B2C2BApi from "../../../api-services/b2c2b-api.service";
import CustomerApi from "../../../api-services/customer-api.service";
import AgentsApi from "../../../api-services/agents-api.service";
import { Agent } from "../../../interfaces/agent.interface";
import PassengerInfo from './PassengerInfo';
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie"
import { whatsappLink, emailLink, defaultPassengerInfos, formatDate, scrollIntoView, SIGNIN_URL, validateBooking, parsePricingTiers } from "../../../utils/helpers";
import { bookingFormValidationMessages } from "../../../utils/antd.props";
import { SubmitButton } from "../../../widgets/SubmitButton";
import { DTA_TYPE } from "../../../enums/dta.enum";
import { BUSINESS_TYPE } from "../../../enums/business_type.enum";
import { useAuth } from "../../../services/AuthContext";
import { PAYMENT_TERMS, pricingCategory } from "../../../enums/pricing.enum";
import { BookingInput } from "../../../interfaces/booking.interface";
import B2C2BPriceDetails from "./B2C2BPriceDetails";
import TermsConsentCheckBox from "./TermsConsentCheckBox";
import PrivacyConsentCheckbox from "./PrivacyConsentCheckbox";
import { PaymentDetails, ChildAgeRanges } from "../../../interfaces/itinerary.interface";

const customCookies = Cookies.withAttributes({
  expires: 14
});

const { Meta } = Card;

interface StickyReferralBinding {
  id?: string | number;
  source?: string;
  email?: string;
  referralEntityId?: number | string;
  referralEntityType?: string;
  referralEntityEmail?: string;
  referralEntityName?: string;
  agent?: Partial<Agent>;
  agency?: any;
  subagent?: Partial<Agent>;
  pendingStickyChangeRequest?: {
    id?: string | number;
    status?: string;
    expires_at?: string;
    message?: string;
  } | null;
}

interface AgentSelectionHistory {
  type: "blank" | "self_select" | "referral_link";
  selectedAgent: any;
  selfSelectedAgent: any;
  preferredAgent: any;
  dtaType: string;
  changeAgent: Record<string, unknown> | null;
}

function withNavigate(Component) {
  return props => <Component {...props} navigate={useNavigate()} params={useParams()} auth={useAuth()}/>;
}

interface BookingSelectionProps {
  bookingForm?: FormInstance; // Optional prop for form instance if needed for external control
  navigate: any; // Consider using NavigateFunction type from react-router-dom
  params: any; // Consider using ReturnType<typeof useParams>
  tourId: number;
  priceCurrency: string;
  prices: any[]; // Define a proper type for price objects
  maxSeats: number;
  departureDate: string;
  auth: any; // Define auth context type
  paxMultiplier: number;
  category: string;
  paymentMethods: any;
  updatePrice: (event: any) => void;
  updatePaymentMethod: (event: any) => void;
  checkoutEnabled?: boolean;
  checkoutDisabledMessage?: string;
  agentChangeReasons?: Record<string, string>;
  travelers?: string[];
  v2?: boolean;
  cabin?: number;
  cabinGroups?: number[];
  isCruise?: boolean;
  agentsData?: Agent[];
  agentStates?: string[];
  countries: any[];
  cities: any[];
  states: any[];
  allowTripleRoom: boolean;
  bookingInputParams: BookingInput;
  paymentDetails?: PaymentDetails;
  childAgeRanges?: ChildAgeRanges;
  updateFareType?: (fareType: PAYMENT_TERMS) => void;
  onSelectTraveler?: () => void;
  isPartner?: boolean;
  hasInfantB2c?: boolean;
}

class BookingSelection extends React.Component<BookingSelectionProps> {
  listApi = new ListApi;
  agentsApi = new AgentsApi;

  changeAgentForm = React.createRef<FormInstance>();
  agentRef = React.createRef<HTMLDivElement>();
  stickyLookupTimeout?: ReturnType<typeof setTimeout>;
  stickyLookupRequestId = 0;

  get isCruise() {
    return !!this.props.isCruise;
  }

  isCustomerSignedIn = () => !!this.props.auth.username && !this.props.isPartner;

  state = {
    allAgents: [],
    agents: [],
    selectedAgent: {id: null, username: ""},
    selfSelectedAgent: {id: null, username: ""},
    showAll: true,
    pax: this.props.paxMultiplier,
    paymentMethods: [],
    banklist_select: [] as any[],
    paymentMethod: {value: "", fieldLabel: ""},
    showChangeAgentModal: false,
    showReasonFreetext: false,
    changeAgent: null as Record<string, unknown> | null,
    preferredAgent: {id: null, link: ""},
    referralCookieAgent: null as any,
    stickyBinding: null as StickyReferralBinding | null,
    stickyBoundAgent: null as Agent | null,
    selectionHistory: null as AgentSelectionHistory | null,
    stickyLookupEmail: "",
    loggedInCustomerEmail: "",
    stickyLookupLoading: false,
    stickyRequestSubmitted: false,
    stickyRequestMessage: "",
    stickyRequestSubmitting: false,
    agentChangeReasons: [],
    paymentChannels: [],
    dtaType: DTA_TYPE.DIRECT,
    submitting: false,
    termsAgreed: false,
    privacyAgreed: false,
    loadingPaymentMethods: true,
    pricingTiers: [],
    pricingCategories: pricingCategory(this.props.category),
    tab: "0",
    selectedFareType: PAYMENT_TERMS.FULL_PAYMENT
  }

  componentDidMount() {
      this.applyReferralCookieAgent();

      this.lookupLoggedInCustomerStickyBinding();

      Promise.all([
        this.listApi.getChangeAgentReasons(this.props.agentChangeReasons),
        this.listApi.getPaymentChannels()
      ]).then(([agentChangeReasons, paymentChannels]) => {
        const newPaymentChannels = paymentChannels.map((channel:any) => ({...channel, options: this.props.paymentMethods[channel.bankName]}));
        this.setState({agentChangeReasons, paymentChannels: newPaymentChannels, loadingPaymentMethods: false})
      })
      .catch(error => {
        console.log(error)
        notification.error({ message: 'Something went wrong.'});
      });

      this.syncPassengersWithTravelers();
      this.buildPricingTiers();
  }

  syncPassengersWithTravelers = () => {
    const { travelers } = this.props;
    if (!Array.isArray(travelers) || travelers.length === 0) return;

    const username = this.isCustomerSignedIn() ? this.props.auth.username : "";
    const updatedPassengers = travelers.map((traveler, index) => {
      const existingPassenger = this.props.bookingForm?.getFieldValue(["passengers", index]) || defaultPassengerInfos(!username && index === 0);
      return {
        ...existingPassenger,
        category: traveler,
        infants: existingPassenger.infants || []
      };
    });

    this.props.bookingForm?.setFieldsValue({ passengers: updatedPassengers });
  }

  buildPricingTiers = () => {
    if (!this.props.prices) return;

    const priceTypes:any[] = [];
    const categoryNames = [...Object.keys(this.state.pricingCategories), "infant"];
    parsePricingTiers(this.props.prices).forEach((pricing) => {
      const { start, end, type } = pricing;
      categoryNames.forEach((categoryName) => {
        const price = pricing[categoryName];
        const dtaKey = `dta_${categoryName}`;
        const dta = pricing[dtaKey] || 0;

        if (price !== undefined && price !== null) {
          priceTypes.push({
            name: categoryName,
            price,
            type,
            dta,
            start,
            end
          });
        }
      });
    });

    this.setState({pricingTiers: priceTypes}, () => this.updatePrice());
  }

  componentWillUnmount() {
    if (this.stickyLookupTimeout) clearTimeout(this.stickyLookupTimeout);
    this.stickyLookupRequestId += 1;
  }

  isValidLookupEmail = (email?: string) => {
    return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  setStateAsync = (state: Record<string, unknown>) => {
    return new Promise<void>((resolve) => this.setState(state as any, resolve));
  }

  getReferralCookieAgent = () => {
    const storedAgency = customCookies.get('agency');
    if(!storedAgency) return null;

    try {
      const {masterAgent, link, subagent, ...agency} = JSON.parse(storedAgency);
      return {
        ...masterAgent,
        link,
        agency,
        subagent
      };
    } catch (_error) {
      customCookies.remove('agency');
      return null;
    }
  }

  applyReferralCookieAgent = () => {
    const referralCookieAgent = this.getReferralCookieAgent();
    if(!referralCookieAgent) return false;

    this.setState({
      referralCookieAgent,
      preferredAgent: referralCookieAgent,
      selectedAgent: referralCookieAgent,
      dtaType: DTA_TYPE.REFERRAL,
      changeAgent: null
    });
    return true;
  }

  normalizeStickyBinding = (response): StickyReferralBinding | null => {
    const data = response?.data || response;
    const attrs = data?.attributes || data;
    if (!attrs) return null;

    return {
      id: data?.id || attrs.id,
      source: attrs.source,
      email: attrs.email,
      referralEntityId: attrs.referral_entity_id || attrs.agent_id || attrs.referral_agent_id,
      referralEntityType: attrs.referral_entity_type || attrs.agent_type,
      referralEntityEmail: attrs.referral_entity_email || attrs.agent_email,
      referralEntityName: attrs.referral_entity_name || attrs.agent_name || attrs.agency_full_name,
      agent: attrs.bound_agent || attrs.agent || attrs.referral_agent,
      agency: attrs.bound_agency || attrs.agency,
      subagent: attrs.bound_subagent || attrs.subagent,
      pendingStickyChangeRequest: attrs.pending_sticky_change_request || attrs.pending_request || null
    };
  }

  normalizeAgentEmail = (email) => {
    if (Array.isArray(email)) return email[0] || "";
    return email || "";
  }

  resolveStickyAgent = (binding: StickyReferralBinding): Agent | null => {
    const agents: Agent[] = this.props.agentsData || [];
    const agentData:any = binding.agent || {};
    const agencyData:any = binding.agency || {};
    const agentId = agentData.id || binding.referralEntityId;
    const numericAgentId = Number(agentId);

    if (agentId && Number.isFinite(numericAgentId)) {
      return {
        id: numericAgentId,
        agency_id: agentData.agency_id || agencyData.id,
        username: agentData.username || agentData.name || binding.referralEntityName || "",
        name: agentData.name || agentData.full_name || binding.referralEntityName || binding.referralEntityEmail || "",
        agency_full_name: agencyData.full_name || agencyData.name || agentData.agency_full_name || binding.referralEntityName || binding.referralEntityEmail || "",
        email: this.normalizeAgentEmail(agentData.email || agentData.emails || agencyData.email || agencyData.emails || binding.referralEntityEmail),
        image: agentData.image,
        phone: agentData.phone || agentData.phones || agencyData.phone || agencyData.phones,
        whatsapp: agentData.whatsapp || agentData.whatsapp_no || agencyData.whatsapp || agencyData.whatsapp_no,
        address: agentData.address || agencyData.address || "",
        city: agentData.city || agencyData.city,
        state: agentData.state || agencyData.state || ""
      };
    }

    const entityId = binding.referralEntityId?.toString();
    const entityType = binding.referralEntityType?.toLowerCase();

    const matchedAgent = agents.find((agent) => {
      if (entityType === "agency" && agent.agency_id != null) {
        return agent.agency_id.toString() === entityId;
      }

      return agent.id?.toString() === entityId;
    });

    if (matchedAgent) return matchedAgent;

    if (!agentId) return null;
    if (!Number.isFinite(numericAgentId)) return null;

    return {
      id: numericAgentId,
      agency_id: agentData.agency_id,
      username: agentData.username || agentData.name || binding.referralEntityName || "",
      name: agentData.name || agentData.full_name || binding.referralEntityName || binding.referralEntityEmail || "",
      agency_full_name: agentData.agency_full_name || binding.referralEntityName || binding.referralEntityEmail || "",
      email: this.normalizeAgentEmail(agentData.email || agentData.emails || binding.referralEntityEmail),
      image: agentData.image,
      phone: agentData.phone || agentData.phones,
      whatsapp: agentData.whatsapp || agentData.whatsapp_no,
      address: agentData.address || "",
      city: agentData.city,
      state: agentData.state || ""
    };
  }

  clearStickyBinding = async () => {
    const selectedAgent:any = this.state.selectedAgent;
    const stickyBoundAgent = this.state.stickyBoundAgent;
    await this.setStateAsync({
      stickyBinding: null,
      stickyBoundAgent: null,
      stickyLookupLoading: false,
      stickyRequestSubmitted: false,
      stickyRequestMessage: "",
      ...(stickyBoundAgent?.id === selectedAgent?.id ? { selectedAgent: {id: null, username: ""}, dtaType: DTA_TYPE.DIRECT } : {})
    });
  }

  buildAgentSelectionHistory = (): AgentSelectionHistory => {
    const {selectedAgent, selfSelectedAgent, preferredAgent, dtaType, changeAgent} = this.state;
    const type = preferredAgent?.id != null
      ? "referral_link"
      : selectedAgent?.id != null
        ? "self_select"
        : "blank";

    return {
      type,
      selectedAgent,
      selfSelectedAgent,
      preferredAgent,
      dtaType,
      changeAgent
    };
  }

  restoreAgentSelectionFallback = async (stickyLookupEmail = "") => {
    const selectionHistory = this.state.selectionHistory;
    const fallbackState:any = {
      stickyBinding: null,
      stickyBoundAgent: null,
      selectionHistory: null,
      stickyLookupEmail,
      stickyLookupLoading: false,
      stickyRequestSubmitted: false,
      stickyRequestMessage: ""
    };

    if (!selectionHistory) {
      await this.setStateAsync(fallbackState);
      return;
    }

    await this.setStateAsync({
      ...fallbackState,
      selectedAgent: selectionHistory.selectedAgent,
      selfSelectedAgent: selectionHistory.selfSelectedAgent,
      preferredAgent: selectionHistory.preferredAgent,
      dtaType: selectionHistory.dtaType,
      changeAgent: selectionHistory.changeAgent
    });
  }

  lookupStickyBinding = async (email?: string, force = false) => {
    const normalizedEmail = email?.trim().toLowerCase() || "";
    const requestId = ++this.stickyLookupRequestId;

    if (!this.isValidLookupEmail(normalizedEmail)) {
      await this.restoreAgentSelectionFallback(normalizedEmail);
      return null;
    }

    if (!force && normalizedEmail === this.state.stickyLookupEmail && !this.state.stickyLookupLoading) {
      return this.state.stickyBoundAgent;
    }

    this.setState({ stickyLookupEmail: normalizedEmail, stickyLookupLoading: true });

    try {
      const response = await B2C2BApi.getCustomerReferralBinding(normalizedEmail);
      if (requestId !== this.stickyLookupRequestId) return null;

      const stickyBinding = this.normalizeStickyBinding(response);
      if (!stickyBinding) {
        if (this.isCustomerSignedIn() && !this.state.selectionHistory) this.applyReferralCookieAgent();
        await this.restoreAgentSelectionFallback(normalizedEmail);
        return null;
      }

      const stickyBoundAgent = this.resolveStickyAgent(stickyBinding);
      if (!stickyBoundAgent) {
        if (this.isCustomerSignedIn() && !this.state.selectionHistory) this.applyReferralCookieAgent();
        await this.restoreAgentSelectionFallback(normalizedEmail);
        return null;
      }

      const selectionHistory = this.state.selectionHistory
        || (!this.state.stickyBinding ? this.buildAgentSelectionHistory() : null);
      const pendingRequest = stickyBinding.pendingStickyChangeRequest;

      await this.setStateAsync({
        stickyBinding,
        stickyBoundAgent,
        selectionHistory,
        selectedAgent: stickyBoundAgent,
        preferredAgent: null,
        dtaType: stickyBinding.source === "self_select" ? DTA_TYPE.DIRECT : DTA_TYPE.REFERRAL,
        changeAgent: null,
        stickyRequestSubmitted: pendingRequest?.status === "pending",
        stickyRequestMessage: pendingRequest?.message || "",
        stickyLookupLoading: false
      });

      return stickyBoundAgent;
    } catch (_error) {
      if (requestId !== this.stickyLookupRequestId) return null;
      if (this.isCustomerSignedIn() && !this.state.selectionHistory) this.applyReferralCookieAgent();
      await this.restoreAgentSelectionFallback(normalizedEmail);
      return null;
    }
  }

  lookupLoggedInCustomerStickyBinding = async () => {
    if (!this.isCustomerSignedIn()) return null;

    try {
      const customer = await CustomerApi.getProfile();
      const email = customer?.email || "";
      this.setState({ loggedInCustomerEmail: email });
      return this.lookupStickyBinding(email);
    } catch (_error) {
      return null;
    }
  }

  handleFormValuesChange = (changedValues, allValues) => {
    if (this.isCustomerSignedIn()) return;

    const firstPassengerEmail = allValues?.passengers?.[0]?.email;
    const firstPassengerChanged = Array.isArray(changedValues?.passengers)
      && changedValues.passengers[0]
      && Object.prototype.hasOwnProperty.call(changedValues.passengers[0], "email");

    if (!firstPassengerChanged) return;
    if (this.stickyLookupTimeout) clearTimeout(this.stickyLookupTimeout);

    this.stickyLookupTimeout = setTimeout(() => {
      this.lookupStickyBinding(firstPassengerEmail);
    }, 500);
  }

  componentDidUpdate(prevProps: Readonly<BookingSelectionProps>, prevState: Readonly<any>, snapshot?: any): void {
    if (prevProps.tourId !== this.props.tourId || prevProps.prices !== this.props.prices) {
      this.buildPricingTiers();
    }

    if (prevProps.travelers !== this.props.travelers && Array.isArray(this.props.travelers)) {
      this.syncPassengersWithTravelers();

      this.buildPricingTiers();
    }
  }

  // Get the price for a specific passenger number and category
  getPriceTierForPassenger = (passengerNumber, category?) => {
    const { pricingTiers } = this.state;
    const candidates = category == undefined ? pricingTiers : pricingTiers.filter((tier:any) => tier.name === category);
    const exact = candidates.find(
      (tier:any) => passengerNumber >= tier.start && passengerNumber <= tier.end
    );
    if (exact) return exact;
    if (candidates.length === 0) return undefined;
    return [...candidates].sort((a:any, b:any) => b.end - a.end)[0];
  };

  updatePrice(){
    const passengers = this.props.bookingForm?.getFieldValue('passengers');
    // Build the items list with all passengers and infants
    const items:any[] = [];
    if (passengers && passengers.length > 0) {
      passengers.forEach((passenger, index) => {
        const passengerIndex = index+1;
        const category = passenger.category;
        if(category == null) return; // Skip if category is not selected
        const priceTier = this.getPriceTierForPassenger(passengerIndex, category);
        const price = priceTier?.price || 0;
        items.push({category, price, price_type: priceTier?.type || ""});

        // // Add infants if they exist
        if (passenger.infants && passenger.infants.length > 0) {
          passenger.infants.forEach((infant, infantIndex) => {
            const infantPrice = this.getPriceTierForPassenger(passengerIndex, 'infant')?.price || 0;
            items.push({category: "infant", price: infantPrice});
          });
        }
      });

      const type = this.getPriceTierForPassenger(passengers.length)?.type;
      this.props.updatePrice({promo: type, items});
      return;
    }
    
    this.props.updatePrice({items});  
  }

  setConsentValue = (field: "terms_agreed" | "privacy_agreed", checked: boolean) => {
    if (field === "terms_agreed") {
      this.setState({ termsAgreed: checked });
    } else {
      this.setState({ privacyAgreed: checked });
    }

    this.props.bookingForm?.setFieldValue(field, checked);
  };

  setFareType = (fareType: PAYMENT_TERMS) => {
    this.setState({selectedFareType: fareType});
    if(this.props.updateFareType) this.props.updateFareType(fareType);
  }

  showPendingStickyRequestModal = (message?: string, expiresAt?: string) => {
    Modal.info({
      title: "Pending change agent request",
      content: (
        <div>
          <p>{message || "You have already submitted a request and it is currently within the cooldown period. Once the cooldown period ends, you will be released from this assignment and may book with a different agent for your next booking."}</p>
          {expiresAt && <p>Cooldown ends at {formatDate(expiresAt, "DD/MM/YYYY HH:mm")}.</p>}
        </div>
      )
    });
  }

  showCustomerBindingMismatchModal = (message?: string) => {
    Modal.info({
      title: "Preferred agency updated",
      content: message || "Your email account is currently assigned to an agent. We will update your preferred agency back to the previously bonded agent."
    });
  }


  validateRequiredFields = (formValues) => {
    scrollIntoView("passenger-info");

    const firstPassengerFields = ["category", "given_name", "surname", "date_of_birth"];
    const otherPassengerFields = ["date_of_birth"];

    const isEmpty = (val) => val === undefined || val === null || val === "";
    
    const passengers = formValues?.passengers || [];
    for (let i = 0; i < passengers.length; i++) {
      const passenger = passengers[i];
      const fieldsToCheck = i === 0 ? firstPassengerFields : otherPassengerFields;

      for (const field of fieldsToCheck) {
        if (isEmpty(passenger?.[field])) {
          this.setState({ tab: String(i) });
          return false;
        }
      }
    }

    return true;
  }


  submitForm = async (formValues) =>{
    //manual checking for required fields
    const validForm = this.validateRequiredFields(formValues);
    if(!validForm) return;

    if (!this.isCruise) {
      const validBooking = validateBooking(this.props.bookingInputParams, false);
      if(validBooking.isValid == false){
        notification.warning({message: validBooking.message});
        return;
      }
    }

    if (this.stickyLookupTimeout) clearTimeout(this.stickyLookupTimeout);
    const staleSelectedAgentId = this.state.selectedAgent?.id;
    let refreshedStickyAgent = null;
    if (this.isCustomerSignedIn()) {
      if (this.state.loggedInCustomerEmail) {
        refreshedStickyAgent = await this.lookupStickyBinding(this.state.loggedInCustomerEmail, true);
      } else {
        refreshedStickyAgent = await this.lookupLoggedInCustomerStickyBinding();
      }
    } else {
      refreshedStickyAgent = await this.lookupStickyBinding(formValues?.passengers?.[0]?.email, true);
    }

    if (refreshedStickyAgent?.id != null && staleSelectedAgentId != null && refreshedStickyAgent.id !== staleSelectedAgentId) {
      this.showCustomerBindingMismatchModal();
      return;
    }
    const {selectedAgent, paymentMethod, changeAgent, dtaType, preferredAgent, selectedFareType} = this.state;

    if(selectedAgent?.id == null){
      notification.warning({message: "Please select an agent."});
      if (this.agentRef.current) {
        this.agentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    else if(!paymentMethod.value){
      notification.warning({message: "Please select payment method before you continue."});
    }
    else{  
      this.setState({submitting: true});
      const priceTypeCount = {};
      const getPricingByType = (priceType): Record<string, unknown>[] => this.state.pricingTiers.filter(({type}) => type === priceType);
      const {mobile, email, member} = formValues.passengers[0];

      const passengers = formValues.passengers?.map((p, index) => {
        const priceTier = this.getPriceTierForPassenger(index+1, p.category);
        const priceType = priceTier?.type;
        priceTypeCount[priceType] = (priceTypeCount[priceType] || 0) + 1;
        const price_types = getPricingByType(priceType).map(({start, end, ...rest}) => rest);
        
        const {as_passport_photocopy} = p;
        if(as_passport_photocopy){
          p.as_passport_photocopy = as_passport_photocopy.response.signed_id;
        }

        const updatedPassengers = { ...p, price_types };

        if (index > 0 && p.same_as_first_pax) {
          const { same_as_first_pax, ...rest } = updatedPassengers; //removes same_as_first_pax from the passenger info to be sent to backend
          return { ...rest, mobile, email };
        }

        return updatedPassengers;
      });

      const bookingSeatsText = Object.entries(priceTypeCount)
        .map(([type, count]) => {
          let adultPrice = 0;
          const pricingByType:any[] = getPricingByType(type);
          if(pricingByType.length > 0) adultPrice = pricingByType[0].price;
          return `${count} ${type}(${adultPrice})`;
        }).join(", ");
      
      const selectedAgentId: number = selectedAgent.id;
      const referralBookingParams = dtaType === DTA_TYPE.REFERRAL && preferredAgent?.id
        ? {
            referral_agent_id: preferredAgent.id,
            new_agent_id: selectedAgentId,
            referral_link: preferredAgent.link
          }
        : {};
      let payment_method = formValues.payment_method;
      let bank_type = "";
      let redirect_url = "";
      
      if (paymentMethod?.value === "dobw" && typeof payment_method === "string") {
        const bankList = this.state.banklist_select || [];
        const found_bank = bankList.find((val: any) => Array.isArray(val) && val[1] === payment_method);
        if (found_bank) {
          redirect_url = found_bank[2] || "";
        }
        const splitString = payment_method.split(" - ");
        payment_method = splitString[0] || payment_method;
        bank_type = splitString[1] || "";
      }

      const payload = {
        tour_id: this.props.tourId,
        agree: formValues.terms_agreed && formValues.privacy_agreed,
        bookings: passengers,
        booking_seats: bookingSeatsText,
        sub_sale_rep: selectedAgent.username || selectedAgent.name || selectedAgent.agency_full_name,
        "payment": {
            "gateway": paymentMethod.value,
            "method": payment_method,
            "bank_type": bank_type,
            "redirect_url": redirect_url
        },
        "set_amount": 0,
        "suka_insurance": null,
        "sg_insurance": null,
        fare_type_selected: selectedFareType,
        ...(selectedFareType === PAYMENT_TERMS.DEPOSIT ? {fare_type: 'deposit'} : {}),
        agent_id: selectedAgentId,
        address_1: formValues.address1,
        address_2: formValues.address2,
        city_name: formValues.city,
        state: formValues.state,
        country: formValues.country,
        postal_code: formValues.postcode,
        register_as_member: member,
        business_type: BUSINESS_TYPE.B2C2B,
        dta_type: dtaType,
        ...referralBookingParams,
        ...(changeAgent ? { ...changeAgent, new_agent_id: selectedAgentId } : {})
      };
      
      B2C2BApi.saveBooking(payload)
        .then((response:any) => {
          if(response.error){
            if (response.error_code === "customer_referral_binding_mismatch") {
              this.showCustomerBindingMismatchModal(response.error);
            } else {
              notification.error({message: response.error})
            }
          }else{
            location.href = response.redirect_to;
          }
          this.setState({submitting: false});
        })
        .catch(err => {
          const errorMessage = err.response?.data?.error || "An unexpected error occurred while submitting your booking. Please try again.";
          if (err.response?.data?.error_code === "customer_referral_binding_mismatch") {
            this.showCustomerBindingMismatchModal(errorMessage);
          } else {
            notification.error({message: errorMessage});
          }
          this.setState({submitting: false});
        });
    }
  }

  handleFormFailed = (errorInfo) => {
    scrollIntoView("passenger-info");
    
    console.log("Validation Failed:", errorInfo);
    const hasErrorsIn = (fields: string[]) => errorInfo.errorFields.some(({name}) => name.some(n => fields.includes(n)));
    if(hasErrorsIn(["address1", "city", "state", "country", "postcode"])){
      this.setState({tab: "booking-address"})
    }
    else if(hasErrorsIn(["payment_method", "terms_agreed", "privacy_agreed"])){
      scrollIntoView("payment-gateway");
    }
    else {
      const passengersError = errorInfo.errorFields.filter(({name}) => name.some(n => n.includes("passengers")));
      if(passengersError.length > 0){
        const paxNo:string = passengersError[0].name[1].toString(); // passenger index;
        this.setState({tab: paxNo})
      }
    }
  }
  
  render(){

    const PassengerCounter = () => {
      var pax = this.state.pax;
      const step = (type:string) =>{
        const form = this.props.bookingForm, fieldName = "passengers";
        const stepSize = this.props.paxMultiplier;
        
        if(type=="up"){
          pax += stepSize;
          // Add stepSize new passengers
          const passengers = form?.getFieldValue(fieldName);
          if (Array.isArray(passengers)) {
            for (let i = 0; i < stepSize; i++) {
              passengers.push(defaultPassengerInfos());
            }
          }
        }else{
          pax -= stepSize;
          // Remove stepSize passengers from the end
          form?.getFieldValue(fieldName)?.splice(pax, stepSize);
        }
        
        form?.setFieldsValue({
          [fieldName]: [...form?.getFieldValue(fieldName)],
        });
    
        this.setState({pax});
        this.updatePrice();
      }

      return (
        <div className="pax-count">
          <label>No. of Pax</label>
          <div className="counter">
            <Button 
              disabled={pax == this.props.paxMultiplier} 
              icon={<Icon path={mdiMinus} size="28px" />} 
              color="default" 
              variant="text" 
              onClick={()=>step("down")}
            />
            <span> {pax} </span>
            <Button 
              disabled={pax == this.props.maxSeats} 
              icon={<Icon path={mdiPlus} size="28px" />} 
              color="default" 
              variant="text" 
              onClick={()=>step("up")}
            />
          </div>
        </div>
      );
    }

    const AgentSelection = () => {
      const {
        preferredAgent,
        stickyBinding,
        stickyBoundAgent,
        stickyLookupLoading,
        agents,
        selectedAgent,
        showReasonFreetext,
        agentChangeReasons
      } = this.state;
      const isStickyMode = !!stickyBinding && !!stickyBoundAgent;
      
      const findAgents = (selectedState) => {
        const allAgents:Agent[] = this.props.agentsData || [];
        const agents:Agent[] = selectedState === "Klang Valley"
          ? allAgents.filter(a => a.state === "Selangor" || a.state.includes("Kuala Lumpur"))
          : allAgents.filter(a => a.state === selectedState);
        this.setState({agents});
      }

      const stateOptions = [...new Set(this.props.agentStates?.map((state: string) => (
        state === "Selangor" || state.includes("Kuala Lumpur") ? "Klang Valley" : state
      )) || [])].map((state) => ({ value: state, label: state }));
      
      const PreferredAgent = (agent) => {
        const reasons:any[] = agentChangeReasons;
        const stickyAgency:any = isStickyMode ? stickyBinding?.agency : agent.agency;
        const stickyAgencyName = stickyAgency?.full_name || stickyAgency?.name || stickyAgency?.username;
        const agentName = stickyAgencyName || agent.agency_full_name || agent.name || agent.username || agent.email;
        const stickySubagent:any = isStickyMode ? stickyBinding?.subagent : agent.subagent;
        const stickySubagentName = stickySubagent?.full_name || stickySubagent?.name || stickySubagent?.username;
        const contactSource = stickyAgencyName ? stickyAgency : agent;
        const agentEmail = this.normalizeAgentEmail(contactSource?.email || contactSource?.emails || agent.email || agent.emails);
        const agentWhatsapp = contactSource?.whatsapp || contactSource?.whatsapp_no || agent.whatsapp || agent.whatsapp_no;
        const phoneValue = contactSource?.phone || contactSource?.phones || agent.phone || agent.phones;
        const phone = Array.isArray(phoneValue) ? phoneValue[0] : (phoneValue || "");

        const closeModal = () => {
          this.setState({showChangeAgentModal:false})
        }

        const showFreeText = (selected) => {
          this.setState({showReasonFreetext: selected === "others"})
        }

        const openChangeAgentModal = () => {
          if (isStickyMode && this.state.stickyRequestSubmitted) {
            this.showPendingStickyRequestModal(this.state.stickyRequestMessage);
            return;
          }

          if (isStickyMode && !this.isCustomerSignedIn()) {
            Modal.confirm({
              title: "Sign in required",
              content: "Please sign in to request a change of your current agent assignment.",
              okText: "Sign in",
              cancelText: "Cancel",
              onOk: () => { location.href = SIGNIN_URL; }
            });
            return;
          }

          this.setState({showChangeAgentModal: true});
        }

        const sendRequest = async (formValues) => {
          if (isStickyMode) {
            this.setState({stickyRequestSubmitting: true});

            try {
              const response:any = await B2C2BApi.createStickyAgentChangeRequest({
                change_reason: formValues.reason,
                other_reason: formValues.reason_other
              });
              const successMessage = response?.message || "Your request has been submitted. You will be released from your current agent assignment after the cooldown period unless you cancel the request.";
              const pendingMessage = "You have already submitted a request and it is currently within the cooldown period. Once the cooldown period ends, you will be released from this assignment and may book with a different agent for your next booking.";

              this.changeAgentForm.current?.resetFields();
              this.setState({
                showChangeAgentModal: false,
                showReasonFreetext: false,
                stickyRequestSubmitted: true,
                stickyRequestMessage: pendingMessage,
                stickyRequestSubmitting: false
              });
              notification.success({message: successMessage});
            } catch (err:any) {
              const errors = err.response?.data?.errors;
              const errorMessage = Array.isArray(errors)
                ? errors.join(" ")
                : err.response?.data?.error || "Unable to submit your request. Please try again.";
              if (err.response?.data?.error_code === "pending_request_exists") {
                this.setState({
                  showChangeAgentModal: false,
                  stickyRequestSubmitted: true,
                  stickyRequestMessage: errorMessage,
                  stickyRequestSubmitting: false
                });
                this.showPendingStickyRequestModal(errorMessage, err.response?.data?.pending_request?.expires_at);
                return;
              }

              notification.error({message: errorMessage});
              this.setState({stickyRequestSubmitting: false});
            }

            return;
          }

          const {preferredAgent} = this.state;
          const changeAgent = {
            referral_agent_id: preferredAgent.id,
            referral_link: preferredAgent.link,
            change_reason: formValues.reason,
            other_reason: formValues.reason_other

          }
          Modal.confirm({
            title: "Change preferred agent?",
            content: "Your booking will continue with the newly selected preferred agent after you confirm.",
            okText: "Confirm",
            cancelText: "Cancel",
            onOk: () => {
              this.setState({showChangeAgentModal: false, preferredAgent: null, selectedAgent: null, changeAgent});
            }
          });
        }
        
        return agent && (
          <Flex justify="space-between" align="center" className="preferred-agent">
            <div className="preferred-agent-details">
              <div className="preferred-agent-name"> 
                {agentName} 
                {!!agentWhatsapp && <Button href={whatsappLink(agentWhatsapp)} target="_blank" icon={<Icon path={mdiWhatsapp} size="14px" />} color="primary" variant="text"/>}
                {agentEmail && <Button href={emailLink(agentEmail)} target="_blank" icon={<Icon path={mdiEmailOutline} size="14px" />} color="primary" variant="text"/>}
              </div>
              {stickySubagentName && stickySubagentName !== agentName && (
                <div className="preferred-agent-lock-notice">
                  Travel Advisor: {stickySubagentName}
                </div>
              )}
              {!!phone && <Button href={`tel:${phone}`} className="whatsapp" icon={<Icon path={mdiPhoneInTalkOutline} size="12px" /> } color="primary" variant="outlined"> {phone}</Button>}
              {isStickyMode && (
                <div className="preferred-agent-lock-notice">
                  You're previously assigned to {agentName}, so this booking will be served by them.
                </div>
              )}
              {isStickyMode && this.state.stickyRequestSubmitted && (
                <Alert
                  type="success"
                  showIcon
                  message={this.state.stickyRequestMessage}
                  style={{marginTop: 10}}
                />
              )}
            </div>
            <Button color="primary" variant="text" onClick={openChangeAgentModal}>
              {isStickyMode && this.state.stickyRequestSubmitted ? "Check my request" : "Change Agent"}
            </Button>

            <Modal title={isStickyMode ? "Request to release my agent assignment" : "Change Agent"} open={this.state.showChangeAgentModal} onCancel={closeModal} footer={null} width={800} centered className="change_agent_modal">          
                <Form
                  ref={this.changeAgentForm}
                  autoComplete="off"
                  layout="vertical"
                  variant="filled"
                  validateMessages={bookingFormValidationMessages}
                  onFinish={sendRequest}
                  className="change_agent_modal-form"
                >
                  {isStickyMode && (
                    <p>
                      Your current booking will still be served by {agentName}. After the cooldown period, you'll be released from this assignment and free to book with a different agent on your next booking.
                    </p>
                  )}

                  <Form.Item label="Reason for Changing Agent" name="reason" rules={[{required: true}]}>
                    <Select placeholder="Select" options={reasons} onChange={showFreeText}/>
                  </Form.Item>

                  {
                    showReasonFreetext && (
                      <Form.Item label="Please Specify your Reason" name="reason_other" rules={[{required: true}]}>
                        <Input placeholder="Reason"/>
                      </Form.Item>
                    )
                  }
    
                  <Flex align="center" justify="center">
                    <SubmitButton form={this.changeAgentForm}>
                      {isStickyMode ? (this.state.stickyRequestSubmitting ? "Submitting..." : "Submit request") : "Change Agent"}
                    </SubmitButton>
                  </Flex>
                </Form>
            </Modal>

          </Flex>
        )
      }

      return (
        <div className="agent-selection">
          <div className="b2c2b-section_panel-header">
            <label>{isStickyMode ? "Bonded GD Partner" : "Preferred GD Partner"}</label>
          </div>

          {
            stickyLookupLoading ? (
              <div className="agent-selection_loading">
                <Skeleton active avatar paragraph={{rows: 2}} />
              </div>
            ) : isStickyMode ? PreferredAgent(stickyBoundAgent) : preferredAgent?.id != null ? PreferredAgent(preferredAgent) : (
              <Space size={20} direction="vertical" style={{display: 'flex'}}>
                <div className="agent-selection_filter">
                  <div className="agent-selection_filter-cities">
                    <Form.Item 
                      label="Select State" 
                      name="agent_state" rules={[{required: true}]}
                      labelCol={{ span: this.props.v2 ? 6 : 24 }}
                      labelAlign="left"
                      layout="horizontal">
                      <Select options={stateOptions} onChange={(state)=> findAgents(state)}/>
                    </Form.Item>
                  </div>
                </div>

                {
                  agents.length > 0 && (
                    <div className="agent-selection_list" ref={this.agentRef}>
                      <label>Available Agents</label>
                      <Flex gap={40} wrap>
                      {
                        agents.map((agent:any, index: number) => {
                          const phone = Array.isArray(agent.phone) ? agent.phone[0] : (agent.phone || "");

                          return (
                              <Card
                                key={index} 
                                hoverable
                                cover={<img alt={agent.agency_full_name} src={agent.image} />}
                                className={`agent-card ${(selectedAgent?.id === agent.id)  && "selected"}`}
                                onClick={()=>this.setState({selectedAgent: agent, selfSelectedAgent: agent, dtaType: DTA_TYPE.DIRECT})}
                              >
                                <Meta title={agent.agency_full_name ? <Tooltip title={agent.agency_full_name}><span>{agent.agency_full_name}</span></Tooltip> : undefined} description={
                                  <>
                                    <div className="agent-selection_list-details">
                                      <Space size={4} direction="vertical" style={{display: 'flex'}}>
                                        <Tooltip title={agent.address}><span className="agent-address">{agent.address}</span></Tooltip>
                                        {!!phone && <Button href={`tel:${phone}`} className="whatsapp" icon={<Icon path={mdiPhoneInTalkOutline} size="14px" /> } color="primary" variant="outlined"> {phone}</Button>}
                                        <Space size={5}>
                                          {!!agent.whatsapp && <Button href={whatsappLink(agent.whatsapp)} target="_blank"  icon={<Icon path={mdiWhatsapp} size="14px" />} color="primary" variant="text"/>}
                                          <Button href={emailLink(agent.email)} target="_blank" icon={<Icon path={mdiEmailOutline} size="14px" />} color="primary" variant="text"/>
                                        </Space>
                                      </Space>
                                    </div>
                                  </> 
                                } />
                              </Card>
                          )
                        })
                      }
                      </Flex>

                    </div>
                  )
                }
              </Space>
            )
          }

        </div>
      )
    }

    const PaymentMethods = () => {
      const {paymentMethod, paymentMethods, paymentChannels, selectedFareType} = this.state;
      const paymentDetails = this.props.paymentDetails;

      let paymentMethodOptions = paymentMethods.map((pm:any) => {
        const label = pm[0];
        let disabled = paymentMethod.value == "dobw" && label.includes("(Offline)");
        
        return {value: pm[1], label, disabled: disabled};
      });

      const getPaymentMethods = (paymentMethod) =>{
        this.props.bookingForm?.setFieldValue("payment_method", "");
        this.props.updatePaymentMethod("");

        const selectedPaymentMethod: any = paymentChannels.find(({id}) => id === paymentMethod.id);
        const options = selectedPaymentMethod?.options || [];
        this.setState({
          paymentMethod,
          paymentMethods: options,
          banklist_select: paymentMethod?.value === "dobw" ? options : []
        });
      }  

      return (
        <div id="payment-gateway" className="b2c2b-section">
          
          { 
            paymentDetails && (
              <>
                <h1>Select Payment Terms</h1>
                <div className="payment-terms">            
                  <Button type="default" variant="outlined" onClick={()=>  this.setFareType(PAYMENT_TERMS.DEPOSIT)} className={`payment-terms-box ${selectedFareType === PAYMENT_TERMS.DEPOSIT && 'selected'}`}>
                    <span className="title"> &nbsp; </span>
                    <span className="note"> Secure your booking today, pay the rest later </span>
                    <span className="price"> {paymentDetails.final_deposit} </span>
                    <Alert
                      message={
                       <span>
                        Final payment of <b> {paymentDetails.balance} </b> is due before <b>{paymentDetails.final_payment_date}</b>
                       </span> 
                      }
                      type="warning"
                    />
                  </Button>
                  <Button type="default" variant="outlined" onClick={()=>  this.setFareType(PAYMENT_TERMS.FULL_PAYMENT)} className={`payment-terms-box ${selectedFareType === PAYMENT_TERMS.FULL_PAYMENT && 'selected'}`}>
                    <span className="title"> Pay in full </span>
                    <span className="note">One-time complete payment, nothing more to pay</span>
                    <span className="price"> {paymentDetails.final_total} </span>
                  </Button>
                </div>
              </>
            )
          }



          <h1>Select Payment Method</h1>
          <div className="payment-gateway_agreement">
            <TermsConsentCheckBox
              checked={this.state.termsAgreed}
              onCheckedChange={(checked) => this.setConsentValue("terms_agreed", checked)}
              style={{marginBottom: 8}}
            />

            <PrivacyConsentCheckbox
              checked={this.state.privacyAgreed}
              onCheckedChange={(checked) => this.setConsentValue("privacy_agreed", checked)}
              style={{marginBottom: 0}}
            />
          </div>
          
          <Space size={10} direction="vertical" style={{display: 'flex'}}>
            <div className="payment-gateway_channels">
              {
                paymentChannels.map((channel:any) => (
                  <Button type="default" variant="outlined" onClick={()=> getPaymentMethods(channel)} key={channel.id} className={`payment-gateway_channels-box ${paymentMethod?.value == channel.value && 'selected'}`}>
                    <img src={`/iceholidays-assets/images/payment/${channel.logo}`} alt={channel.label}/>  
                    <span> {channel.label} </span>
                  </Button>
                ))
              }
            </div>
            {
              paymentMethods.length > 0 && (
                <Form.Item label={paymentMethod.fieldLabel} name="payment_method" rules={[{required: true}]}>
                  <Select options={paymentMethodOptions} onChange={this.props.updatePaymentMethod}/>
                </Form.Item>
              )
            }
          </Space>
        </div>
      )
    }

    const PassengerTab = () => {
      const username = this.isCustomerSignedIn() ? this.props.auth.username : "";
  
      const setDOBWithNRICIfMalaysian = (fieldIndex, value) => {
        const dobFieldName = ["passengers", fieldIndex, "date_of_birth"];
        const dobFieldValue = this.props.bookingForm?.getFieldValue(dobFieldName);
        if(!dobFieldValue) this.props.bookingForm?.setFieldValue(dobFieldName, value);
      }
  
      return (
        <PassengerInfo
            form={this.props.bookingForm}
            showRegisterAsMember={!username}
            priceCurrency={this.props.priceCurrency}
            pricingTiers={this.state.pricingTiers}
            departureDate={this.props.departureDate}
            pricingCategories={this.state.pricingCategories}
            updatePrice={()=>this.updatePrice()}
            prefillDOB={setDOBWithNRICIfMalaysian}
            enableAddInfant={!this.isCruise && !!this.props.hasInfantB2c}
            v2={this.props.v2}
            activeTab={this.state.tab}
            setActiveTab={(key) => this.setState({tab: key})}
            countries={this.props.countries}
            cities={this.props.cities}
            states={this.props.states}
            selectedPax={this.props.travelers?.length ?? 0}
            onSelectTraveler={this.props.onSelectTraveler}
            agentLookupLoading={this.state.stickyLookupLoading}
            cabinGroups={this.props.cabinGroups}
            childAgeRanges={this.props.childAgeRanges}
          />
      )
    }

    return this.props.v2 ? (
      <Form
        form={this.props.bookingForm}
        autoComplete="off"
        layout="vertical"
        variant="outlined"
        className="b2c2b"
        scrollToFirstError
        validateMessages={bookingFormValidationMessages}
        onFinish={this.submitForm}
        onFinishFailed={this.handleFormFailed}
        onValuesChange={this.handleFormValuesChange}
        >
          <Flex gap={20} vertical>
            <fieldset>
              <legend>Your Booking</legend>
              
              <div className="fieldset-content">
                {AgentSelection()}
              </div>
            </fieldset>

            {PassengerTab()}
            {PaymentMethods()}
          </Flex>
          <Spin spinning={this.state.submitting} fullscreen />
      </Form>
    ) : (
      <Form
          form={this.props.bookingForm}
          autoComplete="off"
          layout="vertical"
          variant="filled"
          className="b2c2b"
          scrollToFirstError
          validateMessages={bookingFormValidationMessages}
          onFinish={this.submitForm}
          onFinishFailed={this.handleFormFailed}
          onValuesChange={this.handleFormValuesChange}
        >
          <Flex gap={20} vertical>
            <div id="booking-selection" className="b2c2b-section">
              <h1>Your Booking</h1>

              <div className="b2c2b-section_panel">
                <div className="b2c2b-section_panel-header">
                  <label>Booking Selection</label>
                </div>

                <B2C2BPriceDetails paxCount={this.state.pax} priceCurrency={this.props.priceCurrency} prices={parsePricingTiers(this.props.prices)}/>

                {PassengerCounter()}

                {AgentSelection()}
              </div>
              
            </div>
            
            {PassengerTab()}
            {PaymentMethods()}

            <Flex align="center" justify="center">
              <Tooltip
                title={this.props.checkoutEnabled === false ? this.props.checkoutDisabledMessage : ""}
                placement="top"
              >
                <Button
                  htmlType="submit"
                  type="primary"
                  variant="filled"
                  disabled={this.props.checkoutEnabled === false}
                >
                  Checkout
                </Button>
              </Tooltip>
            </Flex>

          </Flex>
          <Spin spinning={this.state.submitting} fullscreen />
      </Form>
    )
  }

}

export default withNavigate(BookingSelection);
